import React, { useMemo } from 'react';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, countProductsInGroup, thumbForGroup } from './catalogGroupUtils';
import { formatDisplayTitle } from './formatProductTitle';
import { useSiteSettings } from './SiteSettingsContext';
import { resolveFeaturedProducts } from './featuredProducts';
import ProductShelfSection from './components/ProductShelfSection';
import ProductShelfCard from './components/ProductShelfCard';
import CollectionShelfCard from './components/CollectionShelfCard';
import { bestCategoryImage } from './catalog/catalogMedia';
/**
 * Home sections driven by the storefront catalog (MySQL → /api/catalog.php).
 */
export default function HomeDynamicCatalog() {
  const { categories, products, allProducts, loading } = useProducts();
  const { settings: site } = useSiteSettings();

  const sortedCats = useMemo(
    () =>
      [...categories].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
    [categories]
  );

  /** All categories — horizontal scroll shelf (Shopify-style collection list). */
  const topCategories = useMemo(() => sortedCats, [sortedCats]);

  const groupTiles = useMemo(() => {
    const tiles = [];
    for (const c of sortedCats) {
      const list = products[c.slug] || [];
      if (!list.length) continue;
      const groups = buildCategoryGroupCards(c, list);
      for (const g of groups) {
        const n = countProductsInGroup(list, g.key);
        if (n === 0) continue;
        const image = thumbForGroup(list, g.key);
        if (!image) continue;
        tiles.push({
          key: `${c.slug}-${g.key}`,
          title: g.label,
          subtitle: `${n} design${n === 1 ? '' : 's'} · ${formatDisplayTitle(c.name || c.slug)}`,
          image: thumbForGroup(list, g.key),
          to: `/shop/${c.slug}/${encodeURIComponent(g.key)}`,
        });
      }
    }
    return tiles;
  }, [sortedCats, products]);

  const spotlight = useMemo(() => groupTiles.slice(0, 12), [groupTiles]);
  const featuredProducts = useMemo(
    () => resolveFeaturedProducts(allProducts, site.featured_products, 8),
    [allProducts, site.featured_products],
  );

  const home = site.page_copy?.home || {};

  if (loading) {
    return (
      <section className="product-shelf home-collections-shelf">
        <div className="container store-page__container py-5 text-center text-muted">
          Loading collections from catalog…
        </div>
      </section>
    );
  }

  return (
    <>
      <ProductShelfSection
        eyebrow={home.collections_eyebrow || 'Browse by category'}
        title={home.collections_title}
        lead={home.collections_lead}
        actionHref="/shop"
        actionLabel={home.collections_action || 'View all collections'}
        layout="scroll"
        className="home-collections-shelf"
        ariaLabel="Shop collections"
      >
        {topCategories.map((c) => {
          const list = products[c.slug] || [];
          const img = bestCategoryImage(list);
          const n = list.length;
          const countLabel =
            n > 0 ? `${n} handcrafted design${n === 1 ? '' : 's'}` : 'Coming soon';
          return (
            <div className="product-shelf__cell" key={c.slug}>
              <CollectionShelfCard
                to={`/shop/${c.slug}`}
                name={formatDisplayTitle(c.name || c.slug)}
                image={img}
                countLabel={countLabel}
              />
            </div>
          );
        })}
      </ProductShelfSection>

      {spotlight.length > 0 ? (
        <ProductShelfSection
          eyebrow={home.shop_by_style_eyebrow || 'Find your style'}
          title={home.shop_by_style_title}
          lead={home.shop_by_style_lead}
          layout="scroll"
          className="home-styles-shelf"
          ariaLabel="Shop by style"
        >
          {spotlight.map((t) => (
            <div className="product-shelf__cell" key={t.key}>
              <CollectionShelfCard
                to={t.to}
                name={t.title}
                image={t.image}
                countLabel={t.subtitle}
                compact
              />
            </div>
          ))}
        </ProductShelfSection>
      ) : null}

      {featuredProducts.length > 0 ? (
        <ProductShelfSection
          eyebrow={home.featured_eyebrow || 'Curated for you'}
          title={home.featured_title}
          lead={home.featured_lead || 'Handpicked pieces from our studio — tap to explore each design.'}
          actionHref="/shop"
          actionLabel={home.featured_action || 'Shop all'}
          layout="scroll"
          className="home-featured-shelf"
          ariaLabel="Featured products"
        >
          {featuredProducts.map((p) => (
            <div className="product-shelf__cell" key={`${p.category}-${p.id}`}>
              <ProductShelfCard product={p} categorySlug={p.category} />
            </div>
          ))}
        </ProductShelfSection>
      ) : null}
    </>
  );
}
